import { Logger } from 'besonders-logger'
import { triggerSync } from './sync-service'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO, { prefix: '[sync-triggers]' }) // eslint-disable-line unused-imports/no-unused-vars

// Opportunistic sync triggers for the "app is open on bad reception" use-case:
//  - foreground/resume  → pull fresh data so it's there when you open the app
//  - connectivity back  → flush anything you wrote while offline
// This is page-thread only (no service worker) — it works identically in the
// Tauri webview and a plain PWA, as long as the app process is alive. True
// push-while-fully-closed (Android WorkManager / webpush) is intentionally out
// of scope here.

// Debounce: visibilitychange + focus + online can fire in quick succession.
const MIN_GAP_MS = 3000
let lastTrigger = 0
let installed = false

function maybeTrigger(reason: string) {
	const now = Date.now()
	if (now - lastTrigger < MIN_GAP_MS) {
		DEBUG(`skip trigger (${reason}) — within ${MIN_GAP_MS}ms of last`)
		return
	}
	lastTrigger = now
	LOG(`triggering sync (${reason})`)
	triggerSync()
}

export function setupSyncTriggers() {
	if (installed) return
	installed = true

	// Warm resume: on Android the webview fires visibilitychange when the app
	// returns to the foreground. This is the main "updates there when I open it" hook.
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') maybeTrigger('foreground')
	})

	// Desktop / focus fallback (some webviews prefer focus over visibilitychange).
	window.addEventListener('focus', () => maybeTrigger('focus'))

	// Connectivity restored — flush pending pushes immediately. Note: on flaky
	// mobile this event is unreliable (navigator.onLine often stays true), which
	// is why the periodic timer + foreground trigger remain the backstop.
	window.addEventListener('online', () => maybeTrigger('online'))

	LOG('installed (foreground / focus / online → sync)')
}
